import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import axios from 'axios';
import { Button, Spin } from 'antd';
import { CheckCircleOutlined } from '@ant-design/icons';

import Box from '~/components/Box';
import Row from '~/components/Row';
import { Table } from '~/components/Table';

import Filter from './filter';
import AttachFileForm from './form/attachFileForm';
import PaymentForm from './form/paymentForm';
import LienWaiverContract from './form/lienWaiverContract';

import { useInvoices } from './useInvoices';
import { sendInvoice } from '~/services/hooks/invoices';
import { invoicesStyles } from './styles';
import errorHandler from '~/Utils/errorHandler';
import { getInvoiceColumns } from './tableColumns';


export default function Invoices({ project }) {
  const { t } = useTranslation();
  const token = useSelector(state => state.auth.token);

  // Custom hook to fetch invoices and manage state
  const { invoices, loading, setFilters, refresh } = useInvoices(project?.id);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [lienWaiverVisible, setLienWaiverVisible] = useState(false);
  const [payVisible, setPayVisible] = useState(false);
  const [selectedInvoices, setSelectedInvoices] = useState([]);
  const [loadingPdf, setLoadingPdf] = useState(false);

  // Sends invoice by e-mail and reloads the list after finishing
  const handleSendInvoice = useCallback(
    async id => {
      try {
        await sendInvoice(id);
        refresh();
      } catch (err) {
        errorHandler(err);
      }
    },
    [refresh]
  );

  // Displays PDF in a new tab/window
  const showFile = useCallback(
    async record => {
      setLoadingPdf(true);
      try {
        const response = await axios({
          url: `${process.env.REACT_APP_BACKEND_URL}/invoices/generateInvoice/${record.id}`,
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
          },
          responseType: 'blob',
        });

        const blob = new Blob([response.data], { type: 'application/pdf' });
        const url = URL.createObjectURL(blob);
        const win = window.open(url, '_blank');

        if (win) {
          win.focus();
        } else {
          errorHandler(new Error('Could not open a new tab to display the PDF.'));
        }
      } catch (err) {
        errorHandler(err);
      } finally {
        setLoadingPdf(false);
      }
    },
    [token]
  );

  // Generates and forces the download of the PDF
  const handleDownload = useCallback(
    async record => {
      setLoadingPdf(true);
      try {
        const response = await axios({
          url: `${process.env.REACT_APP_BACKEND_URL}/invoices/generateInvoice/${record.id}`,
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
          },
          responseType: 'blob',
        });

        const blob = new Blob([response.data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `invoice_${record.id}.pdf`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } catch (err) {
        errorHandler(err);
      } finally {
        setLoadingPdf(false);
      }
    },
    [token]
  );

  // 1) When the user clicks "Attach file", store the record and open the modal
  const onAttachClick = useCallback(record => {
    setSelectedRecord(record);
    setShowForm(true);
  }, []);

  const onLienClick = useCallback(record => {
    setSelectedRecord(record);
    setLienWaiverVisible(true);
  }, []);

  // 2) Memoize columns, injecting handlers and styles
  const columns = useMemo(
    () => {
      return getInvoiceColumns({
        t,
        style: invoicesStyles,
        showFile,
        handleDownload,
        handleSendInvoice,
        onAttachClick,
        onLienClick,
      })
    },
    [t, showFile, handleDownload, handleSendInvoice, onAttachClick, onLienClick]
  );

  // Configures row selection
  const rowSelection = useMemo(
    () => ({
      selectedRowKeys,
      onChange: keys => setSelectedRowKeys(keys),
      getCheckboxProps: record => ({
        disabled: record.paymentDate !== null,
      }),
    }),
    [selectedRowKeys]
  );

  // When clicking "Mark as Paid", store the selected invoices and open the payment modal
  const viewPayment = useCallback(() => {
    const invoiceList = invoices.filter(item => selectedRowKeys.includes(item.id));
    setSelectedInvoices(invoiceList);
    setPayVisible(true);
  }, [invoices, selectedRowKeys]);

  const hasSelected = selectedRowKeys.length > 0;

  return (
    <Spin spinning={loading || loadingPdf} tip="loading">
      <Box>
        {/* Invoice filter */}
        <Filter isLoading={loading} setFilters={setFilters} />

        {/* "Mark as Paid" button */}
        <Row justify="end">
          <Button
            style={invoicesStyles.btnNew.btn}
            type="default"
            disabled={!hasSelected}
            icon={<CheckCircleOutlined />}
            onClick={viewPayment}
          >
            {t('screens:workbook.receipts.data.markAsPaid')}
          </Button>
        </Row>

        {/* Invoices table */}
        <Table
          bordered
          size="small"
          rowKey="id"
          loading={loading}
          columns={columns}
          dataSource={invoices}
          rowSelection={rowSelection}
        />

        {/* Modal to attach file */}
        {showForm && (
          <AttachFileForm
            selectedRecord={selectedRecord}
            visible={showForm}
            onClose={() => setShowForm(false)}
            onSaved={() => {
              refresh();
              setShowForm(false);
            }}
          />
        )}

        {/* Modal to mark payments */}
        {payVisible && (
          <PaymentForm
            selectedInvoices={selectedInvoices}
            visible={payVisible}
            onClose={() => setPayVisible(false)}
            onPaid={() => {
              refresh();
              setPayVisible(false);
            }}
          />
        )}

        {/* Lien Waiver Contract Modal */}
        {lienWaiverVisible && (
          <LienWaiverContract
            visible={lienWaiverVisible}
            onCancel={() => setLienWaiverVisible(false)}
            onSave={data => {
              setLienWaiverVisible(false);
            }}
          />
        )}
      </Box>
    </Spin>
  );
}




